import { Button, Empty, Icon, Panes, Select } from '@wtfalch/design';
import type { MailboxNode, MailError, ThreadPage, ThreadSummary } from '@wtfalch/email/mailbox';
import { walk } from '@wtfalch/email/mailbox';

import { useListNav } from '../shortcuts.tsx';
import { Avatar } from '../ui/Avatar.tsx';
import { FilterIcon } from '../ui/icons.tsx';
import { folderName, formatWhen, LIST_PAGE_SIZE, rowPeople, stripMarkdownLinks } from './format.ts';
import { MailboxIcon } from './icons.tsx';

/**
 * The list column: the mailbox this is (Inbox, Drafts, Sent, Archive, a
 * folder) in its own head rather than a second sidebar, the unread count,
 * a filter and a sort, and the rows themselves.
 */
export function MailList({
  tree,
  mailboxId,
  page,
  loading,
  error,
  sort,
  onSortChange,
  unreadOnly,
  onUnreadOnlyChange,
  selectedThreadId,
  me,
  onSelectMailbox,
  onSelectThread,
  onRefresh,
  onLoadMore,
  onWrite,
  away,
  onSettings,
}: {
  tree: readonly MailboxNode[] | null;
  mailboxId: string | null;
  page: ThreadPage | null;
  loading: boolean;
  error: MailError | null;
  sort: 'newest' | 'oldest';
  onSortChange: (sort: 'newest' | 'oldest') => void;
  unreadOnly: boolean;
  onUnreadOnlyChange: (unreadOnly: boolean) => void;
  selectedThreadId: string | null;
  me: string;
  onSelectMailbox: (mailboxId: string) => void;
  onSelectThread: (threadId: string) => void;
  onRefresh: () => void;
  /** The next page of the list, appended to the rows shown. */
  onLoadMore: () => void;
  /** Draws a floating Write button over the list: a phone's way to start a
   *  message, since the header's own is not shown there. */
  onWrite?: () => void;
  /** Shows a quiet banner under the head while an away reply is running. */
  away?: { until: string | null; onOpen: () => void } | null;
  /** `MailFolders`' own Settings entry (agora#485), shown here too but
   *  only on a phone, where that whole column -- Settings row included --
   *  is hidden and has no other way back. */
  onSettings?: () => void;
}) {
  const flat = tree ? [...walk(tree)] : [];
  const current = flat.find((m) => m.id === mailboxId) ?? null;

  // The shared list navigation (agora#221): `j`/`k` open the next/previous
  // thread, in the order the rows below render.
  const ids = page ? page.items.map((t) => t.id) : [];
  useListNav(ids, selectedThreadId, onSelectThread);

  // The server may not count (`total` undefined): a full page then means
  // there may be more.
  const hasMore = page
    ? page.total === undefined
      ? page.items.length >= LIST_PAGE_SIZE
      : page.items.length < page.total
    : false;

  return (
    <Panes.Column width={340} surface="panel-2" className="mail-list">
      <div className="flex flex-col mail-list-head">
        <div className="flex items-center gap-3">
          {/* The folder column (`MailFolders.tsx`, agora#287) names the open
              mailbox everywhere it is on screen; `mail.css` shows this
              picker again only below the breakpoint where that column is
              gone, so there stays a way to switch mailboxes on a phone. */}
          <Select
            size="sm"
            className="mail-mailbox-picker"
            icon={<MailboxIcon role={current?.role ?? null} />}
            value={mailboxId ?? ''}
            onChange={(e) => onSelectMailbox(e.target.value)}
            aria-label="Mailbox"
          >
            {flat.map((m) => (
              <option key={m.id} value={m.id}>
                {/* One string: the select reads a label out of its children
                    only when that is a string, and shows the raw id otherwise. */}
                {`${'  '.repeat(m.path.length - 1)}${folderName(m)}`}
              </option>
            ))}
          </Select>
          <span className="h3 mail-list-title">{current && folderName(current)}</span>
          <div className="grow min-w-0" />
          {current && current.unreadThreads > 0 && (
            <span className="chip c-out">{current.unreadThreads} unread</span>
          )}
          <Button
            kind="ghost"
            size="sm"
            iconOnly
            aria-label="Check for new mail"
            disabled={loading}
            onPress={onRefresh}
          >
            <span className={loading ? 'inline-flex mail-spin' : 'inline-flex'}>
              <Icon name="refresh" size={18} />
            </span>
          </Button>
          {onSettings && (
            <Button
              kind="ghost"
              size="sm"
              iconOnly
              className="mail-list-settings"
              aria-label="Mail settings"
              onPress={onSettings}
            >
              <Icon name="settings" size={18} />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Select
            size="sm"
            value={unreadOnly ? 'unread' : 'everything'}
            onChange={(e) => onUnreadOnlyChange(e.target.value === 'unread')}
            aria-label="Filter"
            icon={<FilterIcon size={15} />}
          >
            <option value="everything">Everything</option>
            <option value="unread">Unread</option>
          </Select>
          <Select
            size="sm"
            value={sort}
            onChange={(e) => onSortChange(e.target.value as 'newest' | 'oldest')}
            aria-label="Sort"
            icon={<Icon name="clock" size={15} />}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
          </Select>
        </div>
      </div>
      {away && (
        <button type="button" className="mail-away-banner" onClick={away.onOpen}>
          <Icon name="clock" size={14} />
          <span>{away.until ? `Away reply is on until ${away.until}` : 'Away reply is on'}</span>
        </button>
      )}
      <div className="flex flex-col flex-1 overflow-y-auto min-h-0">
        {error && (
          <Empty icon="mail">
            <p className="text-sm text-muted m-0">{error.message}</p>
            <Button size="sm" onPress={onRefresh}>
              Try again
            </Button>
          </Empty>
        )}
        {!error && loading && !page && (
          <p className="text-xs text-muted m-0 p-4 text-center">Loading…</p>
        )}
        {!error && page && page.items.length === 0 && !loading && (
          <p className="text-xs text-muted m-0 p-4 text-center">{emptyText(current, unreadOnly)}</p>
        )}
        {!error &&
          page?.items.map((t) => (
            <MailRow
              key={t.id}
              thread={t}
              selected={t.id === selectedThreadId}
              me={me}
              onClick={() => onSelectThread(t.id)}
            />
          ))}
        {!error && page && hasMore && (
          <Button
            kind="ghost"
            size="sm"
            className="mail-load-more"
            disabled={loading}
            onPress={onLoadMore}
          >
            Load older
          </Button>
        )}
      </div>
      {onWrite && (
        <Button kind="primary" className="mail-fab" aria-label="Write" onPress={onWrite}>
          <Icon name="pen" size={18} />
          <span>Write</span>
        </Button>
      )}
    </Panes.Column>
  );
}

/** What an empty list says it is missing. */
function emptyText(mailbox: MailboxNode | null, unreadOnly: boolean): string {
  if (unreadOnly) return 'No unread mail';
  switch (mailbox?.role) {
    case 'sent':
      return 'Nothing sent yet';
    case 'drafts':
      return 'No drafts';
    case 'trash':
      return 'Trash is empty';
    default:
      return mailbox ? `No mail in ${mailbox.name}` : 'No mail here';
  }
}

function MailRow({
  thread,
  selected,
  me,
  onClick,
}: {
  thread: ThreadSummary;
  selected: boolean;
  me: string;
  onClick: () => void;
}) {
  const unread = thread.unreadCount > 0;
  const { names: who, avatar: avatarSource } = rowPeople(thread, me);
  const classes = ['mailrow', selected && 'on', unread && 'new'].filter(Boolean).join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      aria-current={selected ? 'true' : undefined}
    >
      <span className="flex flex-none items-center w-2 pt-2 mailrow-dot">
        {unread && <i className="block w-2 h-2 rounded-full bg-accent" />}
      </span>
      <Avatar name={avatarSource.name} seed={avatarSource.email} size="sm" />
      {/* A grid (`mail.css`): who, subject and preview each get their own
          cell with an ellipsis, and the date a cell that never shrinks, so
          nothing runs into its neighbour at any width. */}
      <span className="grow min-w-0 mailrow-body">
        <span className="flex items-center gap-3 mailrow-who">
          <span className={unread ? 'font-strong truncate grow min-w-0' : 'truncate grow min-w-0'}>
            {who}
          </span>
          {thread.emailCount > 1 && (
            <span className="text-xs text-muted flex-none whitespace-nowrap">
              {thread.emailCount}
            </span>
          )}
        </span>
        <span className="text-xs text-muted whitespace-nowrap mailrow-date">
          {formatWhen(thread.receivedAt)}
        </span>
        <span className="text-sm truncate mailrow-subject">{thread.subject || '(no subject)'}</span>
        <span className="text-xs text-muted truncate mailrow-preview">
          {thread.isFlagged && <Icon name="star-filled" size={13} />}{' '}
          {thread.labels.map((label) => (
            <span key={label} className="mail-label-chip">
              {label}
            </span>
          ))}
          {stripMarkdownLinks(thread.preview)}
        </span>
      </span>
    </button>
  );
}
